import { getSession } from '@auth0/nextjs-auth0';
import { UserStatus } from '@summer-health/types/user';
import { SignupHeader } from '@summer-health/ui/care-signup';
import { GetServerSidePropsResult } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { Layout } from '../../components/shared/layout';
import { siteDescription, siteTitle } from '../../utils/site-meta-copy';

export default function Invite(): JSX.Element {
  const posthog = usePostHog();
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    // No invite codes are valid at this time
    setError('Invalid invite code');
  };

  const handleEmailSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!email.trim()) {
      setEmailError('Please enter your email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    posthog?.capture('invite_code_waitlist_signup', { email });
    setEmailSubmitted(true);
  };

  return (
    <>
      <Head>
        <title>Enter Invite Code | {siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://care.summerhealth.com" />
        <meta name="twitter:title" content={siteTitle} />
        <meta name="twitter:description" content={siteDescription} />
        <meta name="twitter:image" content="/site-icon-192x192.png" />
        <meta name="twitter:creator" content="@joinsummer" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        <meta property="og:site_name" content={siteTitle} />
        <meta property="og:url" content="https://care.summerhealth.com" />
        <meta property="og:image" content="/site-icon-512x512.png" />
      </Head>
      <Card elevation={0}>
        <CardContent sx={{ padding: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Welcome to Summer Health
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 4 }}
          >
            Enter your invite code to create an account
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              id="invite-code"
              label="Invite Code"
              value={inviteCode}
              onChange={(e): void => {
                setInviteCode(e.target.value);
                setError('');
              }}
              error={Boolean(error)}
              helperText={error}
              color="secondary"
              inputProps={{
                'data-testid': 'invite-code',
              }}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth
              size="large"
            >
              Continue
            </Button>
          </Box>

          <Divider sx={{ my: 4 }} />

          {emailSubmitted ? (
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              Thanks! We&apos;ll be in touch soon.
            </Typography>
          ) : (
            <>
              <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                sx={{ mb: 2 }}
              >
                Don&apos;t have an invite code? Leave your email and we&apos;ll
                reach out.
              </Typography>
              <Box component="form" onSubmit={handleEmailSubmit}>
                <TextField
                  fullWidth
                  id="email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e): void => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  error={Boolean(emailError)}
                  helperText={emailError}
                  color="secondary"
                  inputProps={{
                    'data-testid': 'waitlist-email',
                  }}
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                >
                  Request Access
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

export const getServerSideProps = async (
  ctx,
): Promise<GetServerSidePropsResult<object>> => {
  const session = await getSession(ctx.req, ctx.res);
  if (session) {
    if (session?.user?.status === UserStatus.Registered) {
      return {
        redirect: {
          destination: '/home',
          permanent: false,
        },
      };
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(ctx.locale, ['common'])),
    },
  };
};

Invite.getLayout = function getLayout(
  page: JSX.Element,
  pageProps,
): JSX.Element {
  const header = <SignupHeader />;
  return (
    <Layout header={header} {...pageProps}>
      {page}
    </Layout>
  );
};
